// Shared vocabulary of the portal's data tables — the head, the rows and the
// footer of a list — in the Team accent, so every list reads as one place
// with the rail and the dashboard.
//
// A list built with these keeps its rows on screen while the next page, sort
// or filter loads (tableBusySx, tableProgressSx), wears the solid accent bar
// as its head (tableHeadSx) and rules its rows with a hairline (tableBodySx).
import { alpha } from '@mui/material/styles';
import {
  BRAND_FONT,
  INDIGO,
  INDIGO_BRIGHT,
  INDIGO_INK
} from 'src/layouts/auth/portals';

// ----------------------------------------------------------------------

export const TABLE_FONT = BRAND_FONT;

const isLight = (theme) => theme.palette.mode === 'light';

export const tableAccentFor = (theme) =>
  isLight(theme) ? INDIGO : INDIGO_BRIGHT;

export const tableInkFor = (theme) =>
  isLight(theme) ? INDIGO_INK : INDIGO_BRIGHT;

const tint = (theme, light, dark) =>
  alpha(INDIGO, isLight(theme) ? light : dark);

// The rule between rows: lighter than the theme divider, so twenty rows read
// as a grid and not as a stack of bars.
export const tableRuleFor = (theme) =>
  alpha(theme.palette.grey[500], isLight(theme) ? 0.2 : 0.24);

// ----------------------------------------------------------------------
// The head: the solid accent bar with rounded ends, white bold labels, the
// sort arrow in white. Give the TableContainer `px: 1, pt: 1` so the bar
// sits inset in its card.

export const tableHeadSx = () => ({
  '& .MuiTableCell-head': {
    py: 1.25,
    // 12px between columns rather than 16: a data table earns more by
    // giving the room to the figures than to the gutters.
    px: 1.5,
    bgcolor: INDIGO,
    backgroundImage: 'none',
    color: '#FFFFFF',
    fontFamily: TABLE_FONT,
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: '0.01em',
    lineHeight: 1.4,
    border: 0,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:first-of-type': {
      pl: 2.5,
      borderRadius: '10px 0 0 10px',
      boxShadow: 'none'
    },
    '&:last-of-type': {
      pr: 2.5,
      borderRadius: '0 10px 10px 0',
      boxShadow: 'none'
    }
  },
  // The labels are written as sentences ("Reviewed by", "Follow-up"); the
  // sort label capitalizes every word unless it is told not to.
  '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active':
    { color: '#FFFFFF', textTransform: 'none' },
  '& .MuiTableSortLabel-icon': { color: '#FFFFFF !important', opacity: 0.7 }
});

// ----------------------------------------------------------------------
// The rows: a hairline between them, the accent as a whisper under the one
// the pointer is on.

export const tableBodySx = (theme) => ({
  '& .MuiTableCell-body': {
    py: 1.75,
    px: 1.5,
    borderBottom: `1px solid ${tableRuleFor(theme)}`,
    '&:first-of-type': { pl: 2.5 },
    '&:last-of-type': { pr: 2.5 }
  },
  '& .MuiTableCell-sizeSmall': { py: 0.875 },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 0 },
  '& .MuiTableRow-root': {
    transition: theme.transitions.create('background-color', { duration: 120 })
  },
  '& .MuiTableRow-hover:hover': { bgcolor: tint(theme, 0.05, 0.12) }
});

// While the next page, sort or filter loads, the rows on screen stay where
// they are but step back; the progress line above the head says why.
export const tableBusySx = (busy) => ({
  transition: 'opacity 200ms, filter 200ms',
  ...(busy && { opacity: 0.4, filter: 'saturate(0.6)', pointerEvents: 'none' })
});

// Rendered inside a `position: relative; height: 0` box between the filters
// and the table, so nothing moves when it appears.
export const tableProgressSx = (theme) => ({
  position: 'absolute',
  top: -2,
  left: 0,
  right: 0,
  height: 3,
  zIndex: 3,
  borderRadius: 0,
  bgcolor: tint(theme, 0.14, 0.24),
  '& .MuiLinearProgress-bar': { bgcolor: tableAccentFor(theme) }
});

// ----------------------------------------------------------------------
// The footer: the page controls in the secondary ink, tabular figures.

// Two things had to be taken off MUI for the footer to read as one row.
// First the alignment: the toolbar centres its children, but the select
// carries its own box and line-height, so "Rows" sat a few pixels above the
// "25" beside it. Every part of the row is now pinned to the same 24px line.
// Second the rhythm: MUI ships a 32px margin after the select and a 20px one
// before the arrows, which left the count stranded between two holes. The
// gaps belong to the toolbar — 8px inside a group, 16px between the three
// groups (how many rows · where you are · move).
//
// The row brings no padding of its own: it goes in a Stack carrying
// tableFooterSx, so it lines up with the TableContainer's inset above it.
export const tablePaginationSx = (theme) => ({
  borderTop: 0,
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    height: 56,
    minHeight: 56,
    px: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  '& .MuiTablePagination-spacer': { flex: 'none' },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    m: 0,
    fontSize: 13,
    lineHeight: '24px',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums'
  },
  // the select: the same 24px line as its label, then the gap that separates
  // "how many rows" from the count
  '& .MuiTablePagination-input': { m: 0, mr: 1, height: 24 },
  '& .MuiTablePagination-select': {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '24px',
    py: 0,
    pl: 0.5,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary
  },
  '& .MuiTablePagination-selectIcon': { color: theme.palette.text.secondary },
  '& .MuiTablePagination-actions': {
    m: 0,
    ml: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.25
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    p: 0.75,
    borderRadius: 1.25,
    color: theme.palette.text.secondary,
    '&.Mui-disabled': { color: theme.palette.text.disabled },
    '&:hover': { bgcolor: tint(theme, 0.08, 0.16), color: tableInkFor(theme) }
  }
});

// The one shape a list footer takes: the page controls at the end of the row,
// on the TableContainer's inset, under the rule that closes the grid. Spread
// it on the Stack that wraps the pagination — with justifyContent="flex-end",
// or "space-between" when something (the dense switch) is kept at the left.
export const tableFooterSx = (theme) => ({
  px: 1,
  borderTop: `1px solid ${theme.palette.divider}`
});
